import clsx from 'clsx';
import type { ItemDraft } from '../../hooks/useOrderEditState';
import type { ItemShape } from '../../lib/orders';
import {
  PRIMARY_SKU_REINIGEN,
  getPackagesForPrimary,
  getPrimaryLabel,
} from '../../lib/order-edit-rules';
import { formatCurrency } from '../../lib/format';

interface ItemEditRowProps {
  label: string;
  draft: ItemDraft;
  oldPriceInclBtw?: number | null;
  newPriceInclBtw?: number | null;
  isRemoved?: boolean;
  isAddition?: boolean;
  onChange: (patch: Partial<ItemDraft>) => void;
  onRemove?: () => void;
  onUndoRemove?: () => void;
}

const SHAPE_OPTIONS: Array<{ value: ItemShape; label: string }> = [
  { value: 'rechthoekig', label: 'Rechthoek' },
  { value: 'rond', label: 'Rond' },
  { value: 'overig', label: 'Overig' },
];

export function ItemEditRow({
  label,
  draft,
  oldPriceInclBtw,
  newPriceInclBtw,
  isRemoved,
  isAddition,
  onChange,
  onRemove,
  onUndoRemove,
}: ItemEditRowProps) {
  const priceDiff =
    oldPriceInclBtw != null && newPriceInclBtw != null
      ? newPriceInclBtw - oldPriceInclBtw
      : null;

  return (
    <div
      className={clsx(
        'px-6 py-4 border-b border-slate-100 last:border-b-0',
        isRemoved && 'bg-red-50/50 opacity-60',
        isAddition && 'bg-emerald-50/30'
      )}
    >
      <div className="flex items-start gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-900">{label}</span>
          {draft.primarySku !== PRIMARY_SKU_REINIGEN && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
              {getPrimaryLabel(draft.primarySku)}
            </span>
          )}
          {isRemoved && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">
              Verwijderd
            </span>
          )}
          {isAddition && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              Nieuw
            </span>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-3">
          {priceDiff != null && !isRemoved && (
            <div className="text-right">
              {oldPriceInclBtw != null && oldPriceInclBtw !== newPriceInclBtw && (
                <div className="text-xs text-slate-400 line-through">
                  {formatCurrency(oldPriceInclBtw)}
                </div>
              )}
              <div className="text-sm font-medium text-slate-900">
                {formatCurrency(newPriceInclBtw ?? 0)}
              </div>
              {priceDiff !== 0 && (
                <div
                  className={clsx(
                    'text-xs',
                    priceDiff > 0 ? 'text-emerald-600' : 'text-red-600'
                  )}
                >
                  {priceDiff > 0 ? '+' : ''}
                  {formatCurrency(priceDiff)}
                </div>
              )}
            </div>
          )}
          {isRemoved && onUndoRemove && (
            <button
              type="button"
              onClick={onUndoRemove}
              className="text-xs text-indigo-600 hover:underline"
            >
              Terugzetten
            </button>
          )}
          {!isRemoved && onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label="Verwijderen"
              className="text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {!isRemoved && (
        <>
          <div className="flex items-center gap-4 mb-3">
            {SHAPE_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name={`shape-${label}`}
                  checked={draft.vorm === opt.value}
                  onChange={() => onChange({ vorm: opt.value })}
                />
                {opt.label}
              </label>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            {draft.vorm === 'rechthoekig' && (
              <>
                <NumberInput
                  label="Lengte (cm)"
                  value={draft.lengteCm}
                  onChange={(v) => onChange({ lengteCm: v })}
                />
                <NumberInput
                  label="Breedte (cm)"
                  value={draft.breedteCm}
                  onChange={(v) => onChange({ breedteCm: v })}
                />
              </>
            )}
            {draft.vorm === 'rond' && (
              <NumberInput
                label="Diameter (cm)"
                value={draft.diameterCm}
                onChange={(v) => onChange({ diameterCm: v })}
              />
            )}
            {draft.vorm === 'overig' && (
              <NumberInput
                label="Oppervlakte (m²)"
                value={draft.oppervlakteM2}
                onChange={(v) => onChange({ oppervlakteM2: v })}
                step={0.1}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex flex-col text-xs text-slate-500 gap-1">
              Pakket
              <select
                value={draft.packageSku ?? ''}
                onChange={(e) => onChange({ packageSku: e.target.value || null })}
                className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
              >
                <option value="">— onveranderd —</option>
                {getPackagesForPrimary(draft.primarySku).map((p) => (
                  <option key={p.sku} value={p.sku}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1.5 text-sm pt-4">
              <input
                type="checkbox"
                checked={draft.ondertapijt}
                onChange={(e) => onChange({ ondertapijt: e.target.checked })}
              />
              Ondertapijt
            </label>
          </div>
        </>
      )}
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-500 gap-1">
      {label}
      <input
        type="number"
        min={0}
        step={step ?? 1}
        value={value ?? ''}
        onChange={(e) => {
          const n = e.target.value === '' ? null : Number(e.target.value);
          onChange(Number.isFinite(n as number) ? (n as number) : null);
        }}
        className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
      />
    </label>
  );
}
